import { schemaText } from './text'

/**
 * Generate FAQPage JSON-LD structured data
 * 
 * This function generates FAQPage schema markup from pageBuilder blocks.
 */

interface FAQItem {
  question: string
  answer?: {
    html?: string
    text?: string
  }
}

interface FAQBlock {
  type?: string
  typeHandle?: string
  faqContent?: Array<{
    items?: FAQItem[]
  }>
  heading?: {
    html?: string
  }
  description?: string
}

/**
 * Find FAQ blocks in pageBuilder by typeHandle
 */
function findFAQBlocks(pageBuilder: FAQBlock[] = []): FAQBlock[] {
  return pageBuilder.filter(block => {
    return block?.typeHandle && ['faq', 'faqDelight', 'faqCentered'].includes(block.typeHandle)
  })
}

/**
 * Extract FAQ items from FAQ blocks
 */
function extractFAQItems(faqBlocks: FAQBlock[]): Array<{
  '@type': string
  name: string
  acceptedAnswer: {
    '@type': string
    text: string
  }
}> {
  const items: Array<{
    '@type': string
    name: string
    acceptedAnswer: {
      '@type': string
      text: string
    }
  }> = []

  faqBlocks.forEach(block => {
    const faqContent = block.faqContent?.[0]
    const faqItems = faqContent?.items || []

    faqItems.forEach(item => {
      if (item.question) {
        const answerText = schemaText(item.answer?.html) || schemaText(item.answer?.text)
        
        if (answerText) {
          items.push({
            '@type': 'Question',
            name: schemaText(item.question),
            acceptedAnswer: {
              '@type': 'Answer',
              text: answerText
            }
          })
        }
      }
    })
  })

  return items
}

/**
 * Generate FAQPage JSON-LD structured data from pageBuilder blocks
 */
export function generateFAQPage(pageBuilder: FAQBlock[] = []): {
  '@context': string
  '@type': string
  mainEntity: Array<{
    '@type': string
    name: string
    acceptedAnswer: {
      '@type': string
      text: string
    }
  }>
} | null {
  // Find FAQ blocks in pageBuilder
  const faqBlocks = findFAQBlocks(pageBuilder)

  // If no FAQ blocks found, return null
  if (faqBlocks.length === 0) {
    return null
  }

  // Extract FAQ items
  const faqItems = extractFAQItems(faqBlocks)

  // If no FAQ items found, return null
  if (faqItems.length === 0) {
    return null
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems
  }
}

